/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

//Import Breadcrumb
import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";
import {
  checkDuplicate,
  createSlotType,
  deleteSlotType,
  listSlotType,
  updateSlotType,
} from "./_requests";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useProfile } from "../../Components/Hooks/UserHooks";

const SlotType = () => {
  const { userProfile } = useProfile();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [slotTypes, setSlotTypes] = useState<any>([]);
  const [modal, setModal] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [slotTypeToDeleteId, setSlotTypeToDeleteId] = useState<string>("");
  const [slotTypeToDeleteName, setSlotTypeToDeleteName] = useState<string>("");
  const [duplicateError, setDuplicateError] = useState<string>("");

  const formatTime = (time: string) => {
    if (!time) return "N/A";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const hh = h % 12 || 12;
    return `${hh.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const toggle = useCallback(() => {
    setModal(!modal);
    setDuplicateError("");
  }, [modal]);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModalOpen(!deleteModalOpen);
  }, [deleteModalOpen]);

  useEffect(() => {
    fetchSlotTypes();
  }, []);

  const fetchSlotTypes = async () => {
    try {
      const res: any = await listSlotType();
      setSlotTypes(res.data);
    } catch (error) {
      console.error("Error fetching slot types:", error);
    }
  };

  const onClickDelete = useCallback(
    (slotType: any) => {
      setSlotTypeToDeleteName(slotType.name);
      setSlotTypeToDeleteId(slotType.id);
      toggleDeleteModal();
    },
    [toggleDeleteModal]
  );

  const confirmDelete = async (id: any) => {
    try {
      const res: any = await deleteSlotType(id, userProfile?.userId);
      toast.success(res.message, { autoClose: 3000 });
      fetchSlotTypes();
    } catch (error) {
      toast.error("Error deleting slot type", { autoClose: 3000 });
    }
    toggleDeleteModal();
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: "",
      name: "",
      from_time: "",
      to_time: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      from_time: Yup.string().required("From time is required"),
      to_time: Yup.string().required("To time is required"),
    }),
    onSubmit: async (values: any) => {
      try {
        if (!isEdit) {
          const res: any = await checkDuplicate(values.name);
          if (res.isDuplicate) {
            setDuplicateError("A slot with this name already exists.");
            toast.error("Duplicate slot name detected!", { autoClose: 3000 });
            return;
          }
        }

        const data = {
          name: values.name,
          from_time: values.from_time,
          to_time: values.to_time,
        };

        if (isEdit) {
          (data as any).updated_by = userProfile?.userId;
          const res: any = await updateSlotType(data, values.id);
          toast.success(res.message, { autoClose: 3000 });
        } else {
          (data as any).created_by = userProfile?.userId;
          const res: any = await createSlotType(data);
          toast.success(res.message, { autoClose: 3000 });
        }
        
        fetchSlotTypes();
        validation.resetForm();
        toggle();
      } catch (error) {
        console.error("Error saving slot type:", error);
        toast.error("Something went wrong", { autoClose: 3000 });
      }
    },
  });

  const handleEditClick = useCallback(
    (slotType: any) => {
      validation.setValues({
        id: slotType.id,
        name: slotType.name,
        from_time: slotType.from_time,
        to_time: slotType.to_time,
      });
      setIsEdit(true);
      toggle();
    },
    [toggle, validation]
  );

  const columns = useMemo(
    () => [
      {
        header: "S.No",
        id: "sno",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const { pageIndex, pageSize } = cellProps.table.getState().pagination;
          return pageIndex * pageSize + cellProps.row.index + 1;
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "From Time",
        accessorKey: "from_time",
        enableColumnFilter: false,
        cell: (cellProps: any) => formatTime(cellProps.getValue()),
      },
      {
        header: "To Time",
        accessorKey: "to_time",
        enableColumnFilter: false,
        cell: (cellProps: any) => formatTime(cellProps.getValue()),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="badge bg-success-subtle text-success text-uppercase">
            {cell.getValue() === 1 ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        header: "Created Date",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const date = new Date(cellProps.getValue());
          return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        header: "Action",
        cell: (cellProps: any) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item edit" title="Edit">
                <Link
                  to="#"
                  className="text-primary d-inline-block edit-item-btn"
                  onClick={() => {
                    const data = cellProps.row.original;
                    handleEditClick(data);
                  }}
                >
                  <i className="ri-pencil-fill fs-16"></i>
                </Link>
              </li>
              <li className="list-inline-item" title="Remove">
                <Link
                  to="#"
                  className="text-danger d-inline-block remove-item-btn"
                  onClick={() => {
                    const data = cellProps.row.original;
                    onClickDelete(data);
                  }}
                >
                  <i className="ri-delete-bin-5-fill fs-16"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleEditClick, onClickDelete]
  );

  const addSlotType = () => {
    setIsEdit(false);
    validation.resetForm();
    toggle();
  };

  document.title = " Paal Dappa | Admin panel";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Slot Type" pageTitle="Dashboard" />
          <Row>
            <Col lg={12}>
              <Card id="SlotTypeList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">Slot Type List</h5>
                      </div>
                    </div>
                    <div className="col-sm-auto">
                      <div>
                        <button
                          type="button"
                          className="btn text-white add-btn me-1"
                          style={{
                            backgroundColor: "#28a8df",
                            borderColor: "#28a8df",
                          }}
                          id="create-btn"
                          onClick={addSlotType}
                        >
                          <i className="ri-add-line align-bottom me-1"></i> Add Slot Type
                        </button>
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  <div>
                    <TableContainer
                      columns={columns}
                      data={slotTypes || []}
                      isGlobalFilter={true}
                      customPageSize={10}
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-top table-bordered align-top table-nowrap mb-0"
                      SearchPlaceholder="Search Slot Type..."
                    />
                  </div>

                  <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {!!isEdit ? "Edit Slot Type" : "Add Slot Type"}
                    </ModalHeader>
                    <Form
                      className="tablelist-form"
                      onSubmit={(e: any) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <ModalBody>
                        <div className="mb-3">
                          <Label htmlFor="name-field" className="form-label">
                            Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="name"
                            id="name-field"
                            className="form-control"
                            placeholder="Enter Name"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.name || ""}
                            invalid={
                              validation.touched.name && validation.errors.name
                                ? true
                                : false
                            }
                          />
                          {validation.touched.name && validation.errors.name ? (
                            <FormFeedback type="invalid">
                              {validation.errors.name}
                            </FormFeedback>
                          ) : null}
                          {duplicateError && (
                            <div className="text-danger mt-1 small">
                              {duplicateError}
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="from_time-field" className="form-label">
                            From Time <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="from_time"
                            id="from_time-field"
                            className="form-control"
                            type="time"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.from_time || ""}
                            invalid={
                              validation.touched.from_time && validation.errors.from_time
                                ? true
                                : false
                            }
                          />
                          {validation.touched.from_time && validation.errors.from_time ? (
                            <FormFeedback type="invalid">
                              {validation.errors.from_time}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="to_time-field" className="form-label">
                            To Time <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="to_time"
                            id="to_time-field"
                            className="form-control"
                            type="time"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.to_time || ""}
                            invalid={
                              validation.touched.to_time && validation.errors.to_time
                                ? true
                                : false
                            }
                          />
                          {validation.touched.to_time && validation.errors.to_time ? (
                            <FormFeedback type="invalid">
                              {validation.errors.to_time}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        <div className="hstack gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn text-white add-btn me-1"
                            style={{
                              backgroundColor: "#28a8df",
                              borderColor: "#28a8df",
                            }}
                            onClick={() => {
                              setModal(false);
                            }}
                          >
                            Close
                          </button>

                          <button
                            type="submit"
                            className="btn text-white add-btn me-1"
                            style={{
                              backgroundColor: "#28a8df",
                              borderColor: "#28a8df",
                            }}
                          >
                            {!!isEdit ? "Update" : "Add Slot Type"}
                          </button>
                        </div>
                      </ModalFooter>
                    </Form>
                  </Modal>
                  <ToastContainer closeButton={false} limit={1} />
                </div>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal} centered>
            <ModalHeader toggle={toggleDeleteModal}>
              Confirm Deletion
            </ModalHeader>
            <ModalBody>
              Are you sure you want to delete the Slot Type{" "}
              <strong>{slotTypeToDeleteName}</strong>?
            </ModalBody>
            <ModalFooter>
              <Button
                className="btn text-white add-btn me-1"
                style={{
                  backgroundColor: "#28a8df",
                  borderColor: "#28a8df",
                }}
                onClick={toggleDeleteModal}
              >
                Cancel
              </Button>
              <Button
                className="btn text-white add-btn me-1"
                style={{
                  backgroundColor: "#28a8df",
                  borderColor: "#28a8df",
                }}
                onClick={() => {
                  confirmDelete(slotTypeToDeleteId);
                }}
              >
                Delete
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SlotType;
